import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { RPAScript, RPAJob, RPAActionType, RPAAgent, RPAAgentStatus } from '../../../shared/types-rpa';

const ACCENT = '#f97316';

const ACTION_TYPES: { value: RPAActionType; label: string; icon: string }[] = [
  { value: 'navigate', label: 'Navigate', icon: '🌐' },
  { value: 'click', label: 'Click', icon: '👆' },
  { value: 'type_text', label: 'Type Text', icon: '⌨️' },
  { value: 'screenshot', label: 'Screenshot', icon: '📸' },
  { value: 'extract_text', label: 'Extract Text', icon: '📄' },
  { value: 'wait', label: 'Wait', icon: '⏳' },
  { value: 'key_press', label: 'Key Press', icon: '🔤' },
  { value: 'scroll', label: 'Scroll', icon: '📜' },
  { value: 'file_download', label: 'Download', icon: '⬇️' },
  { value: 'file_upload', label: 'Upload', icon: '⬆️' },
];

const STATUS_COLORS: Record<string, string> = {
  draft: '#6b7280',
  active: '#10b981',
  disabled: '#ef4444',
  queued: '#f59e0b',
  running: '#3b82f6',
  completed: '#10b981',
  failed: '#ef4444',
  cancelled: '#6b7280',
};

type View = 'list' | 'builder' | 'jobs' | 'agents';

interface StepDraft {
  id: string;
  action: RPAActionType;
  selector: string;
  value: string;
  wait_ms: number;
  description: string;
}

export default function RPAPage() {
  const [scripts, setScripts] = useState<RPAScript[]>([]);
  const [jobs, setJobs] = useState<RPAJob[]>([]);
  const [agents, setAgents] = useState<(RPAAgent & { status: RPAAgentStatus })[]>([]);
  const [view, setView] = useState<View>('list');
  const [loading, setLoading] = useState(true);
  const [editingScript, setEditingScript] = useState<RPAScript | null>(null);
  const [selectedScriptId, setSelectedScriptId] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<(RPAAgent & { status: RPAAgentStatus }) | null>(null);
  const [agentToken, setAgentToken] = useState<string | null>(null);
  const [agentTokenExpiry, setAgentTokenExpiry] = useState<string | null>(null);
  const [assigningAgent, setAssigningAgent] = useState<string | null>(null); // agent id being patched

  // Builder state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [steps, setSteps] = useState<StepDraft[]>([]);
  const [status, setStatus] = useState<'draft' | 'active' | 'disabled'>('draft');

  useEffect(() => { loadScripts(); }, []);

  async function loadAgents() {
    try {
      const res = await api.get<{ agents: (RPAAgent & { status: RPAAgentStatus })[] }>('/rpa/agents');
      setAgents(res.agents);
    } catch { /* ignore */ }
  }

  async function generateToken() {
    try {
      const res = await api.post<{ token: string; expires_at: string }>('/rpa/agents/token', {});
      setAgentToken(res.token);
      setAgentTokenExpiry(res.expires_at);
    } catch { /* ignore */ }
  }

  async function removeAgent(id: string) {
    if (!confirm('Remove this agent?')) return;
    try {
      await api.delete(`/rpa/agents/${id}`);
      setAgents(prev => prev.filter(a => a.id !== id));
      if (selectedAgent?.id === id) setSelectedAgent(null);
    } catch { /* ignore */ }
  }

  async function assignScript(agentId: string, scriptId: string) {
    const agent = agents.find(a => a.id === agentId);
    if (!agent) return;
    if (agent.assigned_script_ids.includes(scriptId)) return;
    setAssigningAgent(agentId);
    try {
      const updated = await api.patch<RPAAgent & { status: RPAAgentStatus }>(`/rpa/agents/${agentId}`, {
        assigned_script_ids: [...agent.assigned_script_ids, scriptId],
      });
      setAgents(prev => prev.map(a => a.id === agentId ? updated : a));
      if (selectedAgent?.id === agentId) setSelectedAgent(updated);
    } catch { /* ignore */ }
    setAssigningAgent(null);
  }

  async function unassignScript(agentId: string, scriptId: string) {
    const agent = agents.find(a => a.id === agentId);
    if (!agent) return;
    setAssigningAgent(agentId);
    try {
      const updated = await api.patch<RPAAgent & { status: RPAAgentStatus }>(`/rpa/agents/${agentId}`, {
        assigned_script_ids: agent.assigned_script_ids.filter(id => id !== scriptId),
      });
      setAgents(prev => prev.map(a => a.id === agentId ? updated : a));
      if (selectedAgent?.id === agentId) setSelectedAgent(updated);
    } catch { /* ignore */ }
    setAssigningAgent(null);
  }

  async function loadScripts() {
    setLoading(true);
    try {
      const res = await api.get<{ scripts: RPAScript[] }>('/rpa/scripts');
      setScripts(res.scripts);
    } catch { /* ignore */ }
    setLoading(false);
  }

  async function loadJobs(scriptId?: string) {
    try {
      const url = scriptId ? `/rpa/jobs?script_id=${scriptId}` : '/rpa/jobs';
      const res = await api.get<{ jobs: RPAJob[] }>(url);
      setJobs(res.jobs);
    } catch { /* ignore */ }
  }

  function openBuilder(script?: RPAScript) {
    if (script) {
      setEditingScript(script);
      setName(script.name);
      setDescription(script.description);
      setTargetUrl(script.target_url);
      setSteps(script.steps.map(s => ({
        id: s.id,
        action: s.action,
        selector: s.selector || '',
        value: s.value || '',
        wait_ms: s.wait_ms || 0,
        description: s.description,
      })));
      setStatus(script.status);
    } else {
      setEditingScript(null);
      setName('');
      setDescription('');
      setTargetUrl('');
      setSteps([]);
      setStatus('draft');
    }
    setView('builder');
  }

  function addStep() {
    setSteps([...steps, {
      id: crypto.randomUUID(),
      action: 'navigate',
      selector: '',
      value: '',
      wait_ms: 0,
      description: '',
    }]);
  }

  function updateStep(index: number, field: string, value: string | number) {
    const updated = [...steps];
    (updated[index] as unknown as Record<string, unknown>)[field] = value;
    setSteps(updated);
  }

  function removeStep(index: number) {
    setSteps(steps.filter((_, i) => i !== index));
  }

  function moveStep(index: number, direction: -1 | 1) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    const updated = [...steps];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setSteps(updated);
  }

  async function saveScript() {
    const payload = {
      name,
      description,
      target_url: targetUrl,
      steps: steps.map(s => ({
        id: s.id,
        action: s.action,
        ...(s.selector ? { selector: s.selector } : {}),
        ...(s.value ? { value: s.value } : {}),
        ...(s.wait_ms > 0 ? { wait_ms: s.wait_ms } : {}),
        description: s.description,
      })),
      status,
    };

    try {
      if (editingScript) {
        await api.patch(`/rpa/scripts/${editingScript.id}`, payload);
      } else {
        await api.post('/rpa/scripts', payload);
      }
      await loadScripts();
      setView('list');
    } catch { /* ignore */ }
  }

  async function deleteScript(id: string) {
    if (!confirm('Delete this script?')) return;
    try {
      await api.delete(`/rpa/scripts/${id}`);
      await loadScripts();
    } catch { /* ignore */ }
  }

  async function runScript(id: string) {
    try {
      await api.post(`/rpa/scripts/${id}/run`, {});
      await loadScripts();
    } catch { /* ignore */ }
  }

  function viewJobs(scriptId: string) {
    setSelectedScriptId(scriptId);
    loadJobs(scriptId);
    setView('jobs');
  }

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>RPA Bridge</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>Automate browser-based workflows across your tools</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {view === 'builder' && (
            <button onClick={() => setView('list')} style={btnStyle('#6b7280')}>&#8592; Back to Scripts</button>
          )}
          {view === 'jobs' && (
            <button onClick={() => setView('list')} style={btnStyle('#6b7280')}>&#8592; Back to Scripts</button>
          )}
          {view === 'list' && (
            <button onClick={() => openBuilder()} style={btnStyle(ACCENT)}>+ New Script</button>
          )}
        </div>
      </div>

      {view !== 'builder' && (
        <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
          {(['list', 'agents'] as const).map(v => (
            <button
              key={v}
              onClick={() => { setView(v); if (v === 'agents') loadAgents(); }}
              style={{
                padding: '0.5rem 1rem',
                background: 'none',
                border: 'none',
                borderBottom: view === v ? `2px solid ${ACCENT}` : '2px solid transparent',
                marginBottom: '-2px',
                color: view === v ? ACCENT : '#6b7280',
                fontWeight: view === v ? 700 : 400,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              {v === 'list' ? 'Scripts' : 'Agents'}
            </button>
          ))}
        </div>
      )}

      {view === 'list' && renderList()}
      {view === 'builder' && renderBuilder()}
      {view === 'jobs' && renderJobs()}
      {view === 'agents' && renderAgents()}
    </div>
  );

  function renderList() {
    if (scripts.length === 0) {
      return (
        <div style={emptyStyle}>
          <p style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>No RPA scripts yet</p>
          <p style={{ color: '#6b7280', margin: '0.5rem 0' }}>Create your first automation script to get started.</p>
          <button onClick={() => openBuilder()} style={btnStyle(ACCENT)}>+ New Script</button>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {scripts.map(s => (
          <div key={s.id} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <span style={{ fontWeight: 600 }}>{s.name}</span>
                  <span style={badgeStyle(STATUS_COLORS[s.status])}>{s.status}</span>
                </div>
                <p style={{ color: '#6b7280', fontSize: '0.8125rem', margin: '0.25rem 0' }}>{s.target_url}</p>
                <div style={{ color: '#9ca3af', fontSize: '0.75rem', display: 'flex', gap: '1rem' }}>
                  <span>{s.steps.length} step{s.steps.length !== 1 ? 's' : ''}</span>
                  <span>Runs: {s.run_count}</span>
                  {s.last_run_at && <span>Last: {new Date(s.last_run_at).toLocaleString()}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.375rem' }}>
                <button onClick={() => runScript(s.id)} style={btnSmall('#3b82f6')} title="Run">Run</button>
                <button onClick={() => viewJobs(s.id)} style={btnSmall('#8b5cf6')} title="Jobs">Jobs</button>
                <button onClick={() => openBuilder(s)} style={btnSmall('#6b7280')} title="Edit">Edit</button>
                <button onClick={() => deleteScript(s.id)} style={btnSmall('#ef4444')} title="Delete">Del</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  function renderBuilder() {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={cardStyle}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem' }}>
            {editingScript ? 'Edit Script' : 'New Script'}
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder="My automation" />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value as 'draft' | 'active' | 'disabled')} style={inputStyle}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Target URL</label>
            <input value={targetUrl} onChange={e => setTargetUrl(e.target.value)} style={inputStyle} placeholder="https://app.example.com/login" />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} placeholder="What does this script do?" />
          </div>
        </div>

        {/* Steps */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Steps ({steps.length})</h3>
            <button onClick={addStep} style={btnStyle(ACCENT)}>+ Add Step</button>
          </div>

          {steps.length === 0 && (
            <p style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem 0' }}>No steps. Click &quot;+ Add Step&quot; to begin.</p>
          )}

          {steps.map((step, i) => {
            const actionInfo = ACTION_TYPES.find(a => a.value === step.action);
            return (
              <div key={step.id} style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.5rem', background: '#fafafa' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: ACCENT, minWidth: 20 }}>#{i + 1}</span>
                  <span style={{ fontSize: '1rem' }}>{actionInfo?.icon}</span>
                  <select value={step.action} onChange={e => updateStep(i, 'action', e.target.value)} style={{ ...inputStyle, flex: 'none', width: 140 }}>
                    {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                  <input value={step.description} onChange={e => updateStep(i, 'description', e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Step description" />
                  <button onClick={() => moveStep(i, -1)} style={btnSmall('#6b7280')} disabled={i === 0} title="Move up">&#9650;</button>
                  <button onClick={() => moveStep(i, 1)} style={btnSmall('#6b7280')} disabled={i === steps.length - 1} title="Move down">&#9660;</button>
                  <button onClick={() => removeStep(i)} style={btnSmall('#ef4444')} title="Remove">X</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '0.5rem', paddingLeft: 28 }}>
                  <input value={step.selector} onChange={e => updateStep(i, 'selector', e.target.value)} style={inputStyle} placeholder="CSS selector (e.g. #login-btn)" />
                  <input value={step.value} onChange={e => updateStep(i, 'value', e.target.value)} style={inputStyle} placeholder="Value / text" />
                  <input type="number" value={step.wait_ms} onChange={e => updateStep(i, 'wait_ms', parseInt(e.target.value) || 0)} style={inputStyle} placeholder="Wait ms" />
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={() => setView('list')} style={btnStyle('#6b7280')}>Cancel</button>
          <button onClick={saveScript} style={btnStyle(ACCENT)} disabled={!name || !targetUrl || steps.length === 0}>Save Script</button>
        </div>
      </div>
    );
  }

  function renderAgents() {
    const STATUS_DOT: Record<RPAAgentStatus, string> = { online: '#10b981', offline: '#9ca3af', error: '#ef4444' };

    if (selectedAgent) {
      const agent = selectedAgent;
      const assignedScripts = scripts.filter(s => agent.assigned_script_ids.includes(s.id));
      const unassignedScripts = scripts.filter(s => !agent.assigned_script_ids.includes(s.id));

      return (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <button onClick={() => setSelectedAgent(null)} style={btnStyle('#6b7280')}>&#8592; All Agents</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_DOT[agent.status], display: 'inline-block' }} />
              <span style={{ fontWeight: 700, fontSize: '1rem' }}>{agent.name}</span>
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>{agent.hostname} · {agent.os} · v{agent.version}</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600 }}>Assigned Scripts</h3>
                {unassignedScripts.length > 0 && (
                  <select
                    onChange={e => { if (e.target.value) assignScript(agent.id, e.target.value); e.target.value = ''; }}
                    style={{ ...inputStyle, width: 'auto', fontSize: '0.8125rem' }}
                    disabled={assigningAgent === agent.id}
                    defaultValue=""
                  >
                    <option value="" disabled>+ Assign Script</option>
                    {unassignedScripts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                )}
              </div>
              {assignedScripts.length === 0 && (
                <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>No scripts assigned. Use the dropdown above to assign a script.</p>
              )}
              {assignedScripts.map(s => (
                <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div>
                    <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{s.name}</span>
                    <span style={{ ...badgeStyle(STATUS_COLORS[s.status] || '#6b7280'), marginLeft: '0.5rem' }}>{s.status}</span>
                  </div>
                  <button onClick={() => unassignScript(agent.id, s.id)} style={btnSmall('#ef4444')} disabled={assigningAgent === agent.id}>Remove</button>
                </div>
              ))}
            </div>

            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9375rem', fontWeight: 600 }}>Agent Info</h3>
              <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <tbody>
                  {[
                    ['Status', <span key="s" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_DOT[agent.status], display: 'inline-block' }} />{agent.status}</span>],
                    ['Last Seen', agent.last_seen_at ? new Date(agent.last_seen_at).toLocaleString() : 'Never'],
                    ['Registered', new Date(agent.registered_at).toLocaleString()],
                    ['ID', <span key="id" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{agent.id}</span>],
                  ].map(([label, value]) => (
                    <tr key={label as string}>
                      <td style={{ padding: '0.375rem 0', color: '#6b7280', width: '7rem' }}>{label}</td>
                      <td style={{ padding: '0.375rem 0' }}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>
              Desktop Agents execute RPA scripts on MSP staff machines. Install an agent, then register it with a token.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginLeft: '1rem' }}>
            <button onClick={generateToken} style={btnStyle(ACCENT)}>Generate Registration Token</button>
          </div>
        </div>

        {agentToken && (
          <div style={{ ...cardStyle, marginBottom: '1rem', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <strong style={{ color: '#166534', fontSize: '0.875rem' }}>Registration Token (one-time, valid 15 minutes)</strong>
                <div style={{ fontFamily: 'monospace', fontSize: '0.8125rem', background: '#dcfce7', padding: '0.375rem 0.625rem', borderRadius: '0.25rem', marginTop: '0.375rem', wordBreak: 'break-all', color: '#166534' }}>
                  {agentToken}
                </div>
                {agentTokenExpiry && (
                  <p style={{ margin: '0.25rem 0 0', color: '#15803d', fontSize: '0.75rem' }}>
                    Expires: {new Date(agentTokenExpiry).toLocaleString()}
                  </p>
                )}
              </div>
              <button onClick={() => { setAgentToken(null); setAgentTokenExpiry(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1.25rem', padding: '0 0.25rem' }}>&#x2715;</button>
            </div>
          </div>
        )}

        {agents.length === 0 ? (
          <div style={emptyStyle}>
            <p style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>No agents registered</p>
            <p style={{ color: '#6b7280', margin: '0.5rem 0' }}>
              Generate a registration token above, then run the Bridge Desktop Agent installer and enter the token when prompted.
            </p>
          </div>
        ) : (
          <div style={cardStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  {['Status', 'Name', 'Hostname', 'OS', 'Version', 'Last Seen', ''].map(h => (
                    <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {agents.map(agent => (
                  <tr key={agent.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.625rem 0.75rem' }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_DOT[agent.status], display: 'inline-block' }} title={agent.status} />
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem' }}>
                      <button
                        onClick={() => setSelectedAgent(agent)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: ACCENT, fontWeight: 600, padding: 0 }}
                      >
                        {agent.name}
                      </button>
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem', color: '#6b7280' }}>{agent.hostname}</td>
                    <td style={{ padding: '0.625rem 0.75rem', color: '#6b7280' }}>{agent.os}</td>
                    <td style={{ padding: '0.625rem 0.75rem', color: '#6b7280' }}>v{agent.version}</td>
                    <td style={{ padding: '0.625rem 0.75rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {agent.last_seen_at ? new Date(agent.last_seen_at).toLocaleString() : 'Never'}
                    </td>
                    <td style={{ padding: '0.625rem 0.75rem' }}>
                      <div style={{ display: 'flex', gap: '0.375rem' }}>
                        <button onClick={() => setSelectedAgent(agent)} style={btnSmall('#8b5cf6')}>Detail</button>
                        <button onClick={() => removeAgent(agent.id)} style={btnSmall('#ef4444')}>Remove</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  function renderJobs() {
    const scriptName = scripts.find(s => s.id === selectedScriptId)?.name || 'Unknown';

    return (
      <div>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Jobs for: {scriptName}</h2>

        {jobs.length === 0 && (
          <div style={emptyStyle}>
            <p style={{ margin: 0, color: '#6b7280' }}>No jobs found. Run the script to create one.</p>
          </div>
        )}

        {jobs.map(job => (
          <div key={job.id} style={{ ...cardStyle, marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={badgeStyle(STATUS_COLORS[job.status])}>{job.status}</span>
                <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>Started: {new Date(job.started_at).toLocaleString()}</span>
                {job.completed_at && (
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>Completed: {new Date(job.completed_at).toLocaleString()}</span>
                )}
              </div>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>{job.id.slice(0, 8)}</span>
            </div>

            {job.error && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#fef2f2', borderRadius: '0.25rem', fontSize: '0.8125rem', color: '#b91c1c' }}>
                {job.error}
              </div>
            )}

            {job.step_results.length > 0 && (
              <div style={{ marginTop: '0.75rem', borderTop: '1px solid #e5e7eb', paddingTop: '0.5rem' }}>
                {job.step_results.map((sr, i) => (
                  <div key={sr.step_id} style={{ display: 'flex', gap: '0.5rem', padding: '0.25rem 0', fontSize: '0.8125rem' }}>
                    <span style={{ color: sr.status === 'completed' ? '#10b981' : sr.status === 'failed' ? '#ef4444' : '#6b7280', minWidth: 16 }}>
                      {sr.status === 'completed' ? '\u2713' : sr.status === 'failed' ? '\u2717' : '-'}
                    </span>
                    <span style={{ color: '#374151' }}>Step {i + 1}</span>
                    <span style={{ color: '#9ca3af' }}>{sr.duration_ms}ms</span>
                    {sr.error && <span style={{ color: '#ef4444' }}>{sr.error}</span>}
                    {sr.output && <span style={{ color: '#6b7280' }}>{sr.output}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
}

// Styles
const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  padding: '1rem 1.25rem',
};

const emptyStyle: React.CSSProperties = {
  ...cardStyle,
  textAlign: 'center',
  padding: '3rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '0.25rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.375rem',
  fontSize: '0.875rem',
  outline: 'none',
  boxSizing: 'border-box',
};

function btnStyle(bg: string): React.CSSProperties {
  return {
    padding: '0.5rem 1rem',
    backgroundColor: bg,
    color: '#fff',
    border: 'none',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
  };
}

function btnSmall(bg: string): React.CSSProperties {
  return {
    padding: '0.25rem 0.5rem',
    backgroundColor: bg,
    color: '#fff',
    border: 'none',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
  };
}

function badgeStyle(color: string): React.CSSProperties {
  return {
    display: 'inline-block',
    padding: '0.125rem 0.5rem',
    backgroundColor: color + '18',
    color,
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: 600,
  };
}
